import { Component, computed, effect, inject, Injector, input, OnInit, output, signal } from '@angular/core';
import { MatChip, MatChipRemove } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import { isEqual, pick } from 'lodash';
import { Subject } from 'rxjs';
import {
  EFieldTypes,
  ESqlOperators,
  TDateRangeName,
  TFilterSave,
  TInEffectFilter,
  DateUtilitiesService as WidgetDateUtilities,
} from 'scw-digital-boards-widgets';
import { DATE_FORMAT, DateUtilitiesService, rangeNameToDate } from '../../../../utilities/date-utilities.service';
import { IDateRangePickerDate } from '../../date-range-picker/date-range-picker.interface';
import { AdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/advanced-filter-api-matcher';
import { BoardAdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/board-advanced-filter-api-matcher';
import { ItemCategoryAdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/item-category-advanced-filter-api-matcher';
import { JobAdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/job-advanced-filter-api-matcher';
import { LineAdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/line-advanced-filter-api-matcher';
import { ShiftAdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/shift-advanced-filter-api-matcher';
import { WoAdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/wo-advanced-filter-api-matcher';
import { AdvancedFilterInputsComponent } from '../advanced-filter-inputs/advanced-filter-inputs.component';
import {
  EAdvancedFilterInputMode,
  TAdvancedFilterInputsAutoSubmit,
} from '../advanced-filter-inputs/advanced-filter-inputs.interface';
import {
  IFilterIdentity,
  ISelectedDateRange,
  ISelectedSingleDate,
  TSelectedFilter,
} from '../advanced-filter.interface';
import { AdvancedFilterService } from '../advanced-filter.service';

@Component({
  imports: [MatChip, MatChipRemove, MatIcon, MatTooltip, TranslatePipe, AdvancedFilterInputsComponent],
  providers: [AdvancedFilterService],
  selector: 'app-advanced-filter-item',
  styleUrl: './advanced-filter-item.component.scss',
  templateUrl: './advanced-filter-item.component.html',
})
export class AdvancedFilterItemComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly advancedFilterService = inject(AdvancedFilterService);
  private readonly widgetDateUtilities = inject(WidgetDateUtilities);

  public readonly filter = input.required<TSelectedFilter>();
  public readonly apiFilters = input.required<Record<string, number>>();
  public readonly oneEditOperationAtATimeSubject = input<Subject<symbol> | null>(null);
  public readonly removeClick = output();
  public readonly editSubmit = output<TFilterSave>();
  public readonly hoveredFilterIdentityUpdate = output<IFilterIdentity | null>();
  public readonly makeOnHoldChipVisible = output<void>();
  public readonly deleteOnHoldChip = output<void>();

  public mode = signal<'view' | 'edit'>('view');
  public selectedFilterIdentity: IFilterIdentity | null = null;
  public readonly dataForEdit = computed(
    () => pick(this.filter(), ['value', 'operator', 'field', 'idOfCustom']) as TInEffectFilter,
  );

  private matcher: AdvancedFilterApiMatcher | null = null;

  public readonly label = computed(() => {
    const filter = this.filter();
    const fieldName = this.advancedFilterService.getFieldName(filter.field, filter.idOfCustom);
    const basis = `${fieldName} ${this.advancedFilterService.operatorNames[filter.operator].toLowerCase()}`;

    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (filter.type) {
      case EFieldTypes.apiDropdown:
        if (filter.operator === ESqlOperators.$in) {
          return `${fieldName} (${filter.value.length})`;
        }

        return this.matcher ? `${basis} ${this.matcher.label()}` : '...';
      case EFieldTypes.customApiDropdown:
      case EFieldTypes.customPredefined:
        return `${fieldName} (${filter.value.length})`;
      case EFieldTypes.predefined:
        return filter.operator === ESqlOperators.$in
          ? `${fieldName} (${filter.value.length})`
          : `${basis} ${filter.translatedName}`;
      case EFieldTypes.date:
        return `${basis} ${this.getDateLabel(filter)}`;
      default:
        return `${basis} ${'value' in filter ? filter.value : ''}`;
    }
  });

  public readonly EAdvancedFilterInputMode = EAdvancedFilterInputMode;

  constructor() {
    effect(() => {
      const filter = this.filter();

      if (filter.type === EFieldTypes.apiDropdown) {
        filter.isInvalid?.set(this.isApiDropdownInvalid());
      }
    });

    effect(() => {
      const oneEditOperationAtATimeSubject = this.oneEditOperationAtATimeSubject();

      if (this.mode() === 'edit' && oneEditOperationAtATimeSubject && this.filter()) {
        oneEditOperationAtATimeSubject.next(this.filter().id);
      }
    });
  }

  public ngOnInit(): void {
    this.matcher = this.createApiMatcherInstance();
    const oneEditOperationAtATimeSubject = this.oneEditOperationAtATimeSubject();

    if (oneEditOperationAtATimeSubject) {
      oneEditOperationAtATimeSubject.subscribe((filterItemId) => {
        if (filterItemId !== this.filter().id) {
          this.dismissEdit();
        }
      });
    }
  }

  public submitEdit({ isValid, data }: TAdvancedFilterInputsAutoSubmit): void {
    if (
      !isValid ||
      data === null ||
      (isEqual(this.dataForEdit().field, data.field) &&
        isEqual(this.dataForEdit().operator, data.operator) &&
        isEqual(this.dataForEdit().value, data.value))
    ) {
      return;
    }

    this.hoveredFilterIdentityUpdate.emit(null);
    this.editSubmit.emit(data);
  }

  public dismissEdit(): void {
    this.hoveredFilterIdentityUpdate.emit(null);
    this.mode.set('view');
  }

  public adjustDate(amount: 1 | -1): void {
    const filter = this.filter();
    let originalDates: IDateRangePickerDate<Dayjs>;

    if (filter.operator === ESqlOperators.$between) {
      originalDates = {
        endDate: dayjs.tz(filter.value.endDate),
        startDate: dayjs.tz(filter.value.startDate),
      };
    } else if (filter.operator === ESqlOperators.$period) {
      originalDates = rangeNameToDate[filter.value as TDateRangeName]();
    } else {
      return;
    }

    const newDates = DateUtilitiesService.adjustDate(amount, originalDates);
    const predefinedEquivalentIfAny = this.widgetDateUtilities.getPredefinedEquivalentIfAny(
      newDates.startDate?.format('YYYY-MM-DD') ?? '',
      newDates.endDate?.format('YYYY-MM-DD') ?? '',
      false,
    );

    this.editSubmit.emit(
      predefinedEquivalentIfAny
        ? { ...filter, operator: ESqlOperators.$period, value: predefinedEquivalentIfAny }
        : {
            ...filter,
            operator: ESqlOperators.$between,
            value: {
              endDate: newDates.endDate!.format(DATE_FORMAT),
              startDate: newDates.startDate!.format(DATE_FORMAT),
            },
          },
    );
  }

  public convertToChip(): void {
    this.oneEditOperationAtATimeSubject()?.next(Symbol());
  }

  private createApiMatcherInstance(): AdvancedFilterApiMatcher | null {
    const filter = this.filter();

    if (filter.type !== EFieldTypes.apiDropdown || filter.operator === ESqlOperators.$in) {
      return null;
    }

    switch (filter.matcherName) {
      case 'lineId':
        return new LineAdvancedFilterApiMatcher(filter.value, this.apiFilters, this.injector);
      case 'boardId':
        return new BoardAdvancedFilterApiMatcher(filter.value, this.apiFilters, this.injector);
      case 'itemCategoryId':
        return new ItemCategoryAdvancedFilterApiMatcher(filter.value, this.apiFilters, this.injector);
      case 'jobId':
        return new JobAdvancedFilterApiMatcher(filter.value, this.apiFilters, this.injector);
      case 'shiftId':
        return new ShiftAdvancedFilterApiMatcher(filter.value, this.apiFilters, this.injector);
      case 'workOrderId':
        return new WoAdvancedFilterApiMatcher(filter.value, this.apiFilters, this.injector);
      default:
        throw 'Not all advanced filter matchers are defined';
    }
  }

  private isApiDropdownInvalid(): boolean | null {
    if (this.filter().type !== EFieldTypes.apiDropdown) {
      return false;
    }

    return this.matcher?.isInvalid() ?? null;
  }

  private getDateLabel(filter: ISelectedDateRange | ISelectedSingleDate): string {
    if (filter.operator === ESqlOperators.$between) {
      return filter.value.startDate === filter.value.endDate
        ? DateUtilitiesService.formatIntoUserPreferenceWithoutTimezoneChange(filter.value.startDate)
        : `${DateUtilitiesService.formatIntoUserPreferenceWithoutTimezoneChange(filter.value.startDate)}-${DateUtilitiesService.formatIntoUserPreferenceWithoutTimezoneChange(filter.value.endDate)}`;
    }

    return DateUtilitiesService.formatIntoUserPreferenceWithoutTimezoneChange(filter.value);
  }
}
